/**
 * Spend Easy - Net Worth & Asset / Liability Intelligence Engine
 * Computes live balances, categorizes assets vs liabilities, calculates debt health scores,
 * and tracks 6-month historical net worth trajectories.
 */

import { subMonths, endOfMonth, startOfMonth, format, isBefore } from 'date-fns';

export const ACCOUNT_CLASSIFICATIONS = {
  // Assets
  CASH: { key: 'cash', label: 'Cash & Wallet', type: 'asset', icon: 'wallet-outline', color: '#10B981' },
  BANK: { key: 'bank', label: 'Checking & Bank', type: 'asset', icon: 'business-outline', color: '#3B82F6' },
  SAVINGS: { key: 'savings', label: 'Savings & Deposits', type: 'asset', icon: 'shield-checkmark-outline', color: '#6366F1' },
  INVESTMENT: { key: 'investment', label: 'Investments & Stocks', type: 'asset', icon: 'trending-up-outline', color: '#8B5CF6' },
  PROPERTY: { key: 'property', label: 'Real Estate & Assets', type: 'asset', icon: 'home-outline', color: '#14B8A6' },

  // Liabilities
  CARD: { key: 'card', label: 'Credit Card', type: 'liability', icon: 'card-outline', color: '#EF4444' },
  LOAN: { key: 'loan', label: 'Personal & Auto Loan', type: 'liability', icon: 'receipt-outline', color: '#F97316' },
  MORTGAGE: { key: 'mortgage', label: 'Home Mortgage', type: 'liability', icon: 'key-outline', color: '#DC2626' },
};

/**
 * Returns whether an account type is considered an Asset or Liability
 */
export function isLiabilityType(type = '') {
  const t = String(type).toLowerCase();
  return ['card', 'credit', 'creditcard', 'credit card', 'loan', 'mortgage', 'debt', 'liability'].includes(t);
}

/**
 * Computes live account balances based on initial balances and all transactions
 */
export function calculateLiveAccountBalances(accounts = [], transactions = []) {
  const balances = {};

  // 1. Initialize with initial balances
  accounts.forEach((acc) => {
    balances[acc.name] = parseFloat(acc.initialBalance) || 0;
  });

  // 2. Adjust for transactions
  transactions.forEach((tx) => {
    const amt = parseFloat(tx.amount) || 0;
    const accountName = tx.account;

    if (balances[accountName] === undefined) {
      balances[accountName] = 0;
    }

    if (tx.type === 'income') {
      balances[accountName] += amt;
    } else if (tx.type === 'expense') {
      balances[accountName] -= amt;
    } else if (tx.type === 'transfer') {
      // From account loses funds
      balances[accountName] -= amt;
      // To account gains funds
      if (tx.toAccount) {
        if (balances[tx.toAccount] === undefined) {
          balances[tx.toAccount] = 0;
        }
        balances[tx.toAccount] += amt;
      }
    }
  });

  return balances;
}

/**
 * Comprehensive Net Worth Analysis
 */
export function calculateNetWorthIntelligence(accounts = [], transactions = []) {
  const balances = calculateLiveAccountBalances(accounts, transactions);

  const assetAccounts = [];
  const liabilityAccounts = [];

  let totalAssets = 0;
  let totalLiabilities = 0;
  let liquidCash = 0;

  accounts.forEach((acc) => {
    const currentBalance = balances[acc.name] || 0;
    const isLiability = isLiabilityType(acc.type);

    const enrichedAccount = {
      ...acc,
      currentBalance,
      isLiability,
      classification: isLiability ? 'liability' : 'asset',
    };

    if (isLiability) {
      // For credit cards and loans, liability is the owed balance (positive magnitude)
      const liabilityOwed = currentBalance < 0 ? Math.abs(currentBalance) : (currentBalance > 0 ? currentBalance : 0);
      enrichedAccount.liabilityAmount = liabilityOwed;
      totalLiabilities += liabilityOwed;
      liabilityAccounts.push(enrichedAccount);
    } else {
      const assetVal = currentBalance > 0 ? currentBalance : 0;
      enrichedAccount.assetAmount = assetVal;
      totalAssets += currentBalance; // Allow negative checking if overdrawn

      const t = (acc.type || 'cash').toLowerCase();
      if (['cash', 'bank', 'checking', 'savings'].includes(t)) {
        liquidCash += Math.max(0, currentBalance);
      }
      assetAccounts.push(enrichedAccount);
    }
  });

  const netWorth = totalAssets - totalLiabilities;

  // Debt-to-Asset Health Score
  let debtRatio = totalAssets > 0 ? Math.round((totalLiabilities / totalAssets) * 100) : 0;
  let debtRating = 'Excellent';
  let debtRatingColor = '#10B981';

  if (totalAssets <= 0 && totalLiabilities > 0) {
    debtRatio = 100;
    debtRating = 'High Risk';
    debtRatingColor = '#EF4444';
  } else if (debtRatio > 60) {
    debtRating = 'High Risk';
    debtRatingColor = '#EF4444';
  } else if (debtRatio > 35) {
    debtRating = 'Caution';
    debtRatingColor = '#F59E0B';
  } else if (debtRatio > 15) {
    debtRating = 'Good';
    debtRatingColor = '#3B82F6';
  }

  // 6-Month Historical Net Worth Trajectory
  const trajectory = [];
  const now = new Date();

  for (let i = 5; i >= 0; i--) {
    const monthDate = subMonths(now, i);
    const monthEnd = endOfMonth(monthDate);
    const monthLabel = format(monthDate, 'MMM yyyy');

    // Filter transactions up to monthEnd
    const txUpToMonth = transactions.filter((t) => {
      const d = t.date instanceof Date ? t.date : new Date(t.date);
      return isBefore(d, monthEnd);
    });

    const monthBalances = calculateLiveAccountBalances(accounts, txUpToMonth);
    let monthAssets = 0;
    let monthLiabilities = 0;

    accounts.forEach((acc) => {
      const b = monthBalances[acc.name] || 0;
      if (isLiabilityType(acc.type)) {
        monthLiabilities += b < 0 ? Math.abs(b) : (b > 0 ? b : 0);
      } else {
        monthAssets += b;
      }
    });

    const monthNetWorth = monthAssets - monthLiabilities;

    trajectory.push({
      month: monthLabel,
      netWorth: Math.round(monthNetWorth),
      assets: Math.round(monthAssets),
      liabilities: Math.round(monthLiabilities),
    });
  }

  // MoM Net Worth Growth
  const currentMonthNW = trajectory[trajectory.length - 1]?.netWorth || 0;
  const prevMonthNW = trajectory[trajectory.length - 2]?.netWorth || 0;
  const momGrowthAmount = currentMonthNW - prevMonthNW;
  const momGrowthPct = prevMonthNW !== 0 ? Math.round(((currentMonthNW - prevMonthNW) / Math.abs(prevMonthNW)) * 100) : 0;

  return {
    netWorth,
    totalAssets,
    totalLiabilities,
    liquidCash,
    debtRatio,
    debtRating,
    debtRatingColor,
    momGrowthAmount,
    momGrowthPct,
    assetAccounts,
    liabilityAccounts,
    allBalances: balances,
    trajectory,
    accountCount: accounts.length,
  };
}
